import moment from "moment";
import { SagaIterator } from "redux-saga";
import { call, delay, put, takeLatest } from "redux-saga/effects";

import { callGet, callPost, externalCallGet } from "../../http_client";
import { Cookie } from "../../utils/browser";
import { defaultHost, marketToHostMap } from "../common_types";

import {
  fetchDebugResultsError,
  fetchDebugResultsRequest,
  fetchDebugResultsSuccess,
  startSearchError,
  startSearchRequest,
  startSearchSuccess,
  unwrapLinkRequest,
} from "./actions";
import { DebugActionTypes } from "./types";

export function* handleUnwrapLink(action: ReturnType<typeof unwrapLinkRequest>): SagaIterator {
  const { onUnwrapSearch, ...payload } = action.payload;
  try {
    const { data } = yield call(callGet, "/debug/unwrap-link", payload);
    if (onUnwrapSearch !== undefined) {
      yield call(onUnwrapSearch, {
        ...data,
        departureDate: moment(data.departureDate),
        returnDate: data.returnDate ? moment(data.returnDate) : undefined,
      });
    }
  } catch (err) {
    yield call(onUnwrapSearch, undefined);
  }
}

function* handleFetchAUID(market?: string): SagaIterator {
  const hostname = marketToHostMap.get(market!) || defaultHost;

  if (market === "ru") {
    const cookieAUID = yield call(Cookie.get, "auid");

    if (cookieAUID) {
      return cookieAUID;
    }
  }

  try {
    const response = yield call(externalCallGet, `https://${hostname}/auid`);
    return response.data.auid;
  } catch {
    /* ¯\_(ツ)_/¯ */
  }

  return undefined;
}

function* handleStartSearch(action: ReturnType<typeof startSearchRequest>): SagaIterator {
  const { gateId, departureDate, returnDate, market, ...data } = action.payload;

  try {
    const auid = yield call(handleFetchAUID, market?.trim().toLowerCase());

    const response = yield call(
      callPost,
      "/debug/start-search",
      {
        ...data,
        auid,
        market,
        departureDate: departureDate.format("YYYY-MM-DD"),
        returnDate: returnDate?.format("YYYY-MM-DD"),
      },
      { gateId }
    );
    yield put(startSearchSuccess());
    yield put(fetchDebugResultsRequest(response.data));
  } catch (err) {
    switch (err.response.status) {
      case 403:
        yield put(startSearchError({}, err.response.data));
        break;
      default:
        yield put(startSearchError(err.response.data));
    }
  }
}

function* handleFetchDebugResults(action: ReturnType<typeof fetchDebugResultsRequest>): SagaIterator {
  try {
    const { taskId } = action.payload;
    yield delay(2000);
    const response = yield call(callGet, `/debug/results/${taskId}`);
    switch (response.status) {
      case 200:
        yield put(fetchDebugResultsSuccess(response.data));
        break;
      default:
        yield put(fetchDebugResultsRequest(action.payload));
    }
  } catch (err) {
    const { data } = err.response;
    yield put(fetchDebugResultsError(data.detail));
  }
}

function* watchDebugSagas(): SagaIterator {
  yield takeLatest(DebugActionTypes.UNWRAP_LINK_REQUEST, handleUnwrapLink);
  yield takeLatest(DebugActionTypes.START_SEARCH_REQUEST, handleStartSearch);
  yield takeLatest(DebugActionTypes.FETCH_DEBUG_SEARCH_RESULTS_REQUEST, handleFetchDebugResults);
}

export default watchDebugSagas;
